import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { isHrHomeUser } from '@/lib/permissions';
import PipelineModule from '@/components/PipelineModule';
import BDModule from '@/components/BDModule';
import BriefcaseTile from '@/components/BriefcaseTile';
import EditableTarget from '@/components/EditableTarget';
import LegalComplianceTab from '@/components/LegalComplianceTab';
import ContractTable from '@/components/ContractTable';
import PeopleTab from '@/components/PeopleTab';
import ReportingTree from '@/components/ReportingTree';
import ReflectionCard from '@/components/ReflectionCard';
import ClientTab from '@/components/ClientTab';
import DivisionRealisedLedger from '@/components/DivisionRealisedLedger';
import MissionTab from '@/components/MissionTab';
import MonthlyBillingTab from '@/components/MonthlyBillingTab';
import InvoiceSLATracker from '@/components/InvoiceSLATracker';
import DailySalesTab from '@/pages/DailySalesPage';
import BookingPipeline from '@/pages/BookingPipeline';
import CostModule from '@/pages/CostModule';
import VendorModule from '@/pages/VendorModule';
import GroupFoodCostTile from '@/components/GroupFoodCostTile';
import MonthlyArchive from '@/components/MonthlyArchive';
import StaysIntelTile from '@/components/StaysIntelTile';
import SyncStatusBar from '@/components/SyncStatusBar';
import AnalyticsTab, { MiniLeaderboard, MiniRevenueTrend, MiniContribution, PeopleWarnings, WinsAndMomentum } from '@/components/AnalyticsWidgets';
import { CostSummaryWidget } from '@/components/CostWidgets';
import RevenueTrendWidget from '@/components/RevenueTrendWidget';
import { DivisionGapScreamer } from '@/components/GapScreamer';
import DivisionDeltaChip from '@/components/DivisionDeltaChip';
import PaceIndicator from '@/components/PaceIndicator';
import DivisionTrainingCenter from '@/components/DivisionTrainingCenter';
import LiveTicker from '@/components/LiveTicker';
import WhatsNewStrip from '@/components/WhatsNewStrip';
import ChannelMatrix from '@/components/ChannelMatrix';
import TDBMissingDaysTicker from '@/components/TDBMissingDaysTicker';
import TargetPaceLifebar from '@/components/TargetPaceLifebar';
import TDBLeaderPulse from '@/components/TDBLeaderPulse';
import TDBPnLBreakdown from '@/components/TDBPnLBreakdown';
import TDBLiveMTDPnL from '@/components/TDBLiveMTDPnL';
import TDBReconciliationDrift from '@/components/TDBReconciliationDrift';
import TDBExportMIS from '@/components/TDBExportMIS';
import TDBUserManuals from '@/components/TDBUserManuals';
import TDBProdBootstrap from '@/components/TDBProdBootstrap';
import TDBIngredientMasterTile from '@/components/TDBIngredientMasterTile';
import TDBRecipeEditorTile from '@/components/TDBRecipeEditorTile';
import TDBBundleBOMTile from '@/components/TDBBundleBOMTile';
import DailySalesGrid from '@/components/DailySalesGrid';
import DailyExpenseEntry from '@/components/DailyExpenseEntry';
import TDBStockRegister from '@/components/TDBStockRegister';
import TDBMonthCloseCheck from '@/components/TDBMonthCloseCheck';
import TDBShopifyUnallocated from '@/components/TDBShopifyUnallocated';
import TDBProductBostonMatrix from '@/components/TDBProductBostonMatrix';
import TDBRecipesEditor from '@/components/TDBRecipesEditor';
import TDBFoodCostKPI from '@/components/TDBFoodCostKPI';
import MonthPicker, { monthBoundaries, defaultCurrentYM } from '@/components/MonthPicker';
import UnattributedOrdersCard from '@/components/UnattributedOrdersCard';
import MarketingModule from '@/components/MarketingModule';
import MEMarketingModule from '@/components/MEMarketingModule';
import SalesModule from '@/components/SalesModule';
import { FileEdit, MessageSquare, Send, ChevronLeft, ExternalLink } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';

// Clickable outlet name — opens Outlet Intelligence page.
// For B2B divisions (lc / cc-clubs / cc-ops / cs) where "outlets" are
// actually clients, we route to the Client Dossier page instead so the
// CEO lands on Contracts/Contacts/Documents/Meeting Notes/Payments
// instead of the operations-style Outlet Intel tabs (SOPs, Compliance).
const B2B_DIVS = ['lc', 'cc', 'cc-clubs', 'cc-ops', 'cs', 'cs-b2b', 'cs-b2c'];

function OutletLink({ name, divId, className = '', children }) {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(null);
  const [checked, setChecked] = useState(false);

  // For B2B divisions, look up the matching client row once and cache.
  useEffect(() => {
    if (!name || checked) return;
    if (!B2B_DIVS.includes(divId)) { setChecked(true); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/clients-registry?division_id=${divId}`);
        if (!alive) return;
        const norm = (s) => (s || '').toLowerCase().trim();
        const match = (r.data || []).find(
          (c) => norm(c.client_name) === norm(name) || norm(c.name) === norm(name)
        );
        if (match && (match.id || match._id)) setClientId(match.id || match._id);
      } catch { /* silent — fall back to outlet route */ }
      if (alive) setChecked(true);
    })();
    return () => { alive = false; };
  }, [name, divId, checked]);

  if (!name) return children || name || '—';
  const handleClick = (e) => {
    e.stopPropagation();
    if (clientId) {
      navigate(`/client/${clientId}`);
    } else {
      navigate(`/division/${divId}/outlet/${encodeURIComponent(name)}`);
    }
  };
  return (
    <span onClick={handleClick} className={`cursor-pointer hover:text-[#2A7B88] hover:underline transition-colors ${className}`} data-testid={`outlet-link-${name.replace(/\s+/g, '-')}`}>
      {children || name}
    </span>
  );
}

const formatCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) {
    const cr = v / 10000000;
    // Show enough precision: 2.025Cr not 2.02Cr
    const s = cr.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    return `${s}Cr`;
  }
  if (v >= 100000) {
    const l = v / 100000;
    const s = l.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return `${s}L`;
  }
  return Number(v).toLocaleString('en-IN');
};

export default function DivisionDetail({ overrideDivId, hideHeader = false }) {
  const params = useParams();
  const divId = overrideDivId || params.divId;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [division, setDivision] = useState(null);
  const [divStats, setDivStats] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [activeTab, setActiveTab] = useState(user?.role === 'cost_controller' ? 'cost' : 'overview');
  const [clubsRevenue, setClubsRevenue] = useState(null);
  const [quarterStats, setQuarterStats] = useState(null);
  const [periodView, setPeriodView] = useState('quarterly');
  // Month being viewed when periodView==='monthly'. Defaults to the current
  // IST month. The MonthPicker dropdown lets the user scroll back to
  // April / May / etc. (TDB launched on ScaleBoard in Apr 2026.)
  const [selectedMonth, setSelectedMonth] = useState(defaultCurrentYM());
  const [selectedOutlet, setSelectedOutlet] = useState('all');
  const [outlets, setOutlets] = useState([]);
  const [outletScores, setOutletScores] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshAll = () => setRefreshTick((t) => t + 1);

  const isCeo = user?.role === 'ceo';
  const isSiteManager = user?.role === 'site-manager';
  const isSubLeader = user?.role === 'sub_leader';
  const isCostController = user?.role === 'cost_controller';
  const isScopedView = isSiteManager || isSubLeader;
  const siteFilter = user?.site || (isSubLeader && user?.sub_view?.scope_type === 'location' ? user?.sub_view?.scope_value?.[0] : null);

  useEffect(() => {
    setLoading(true);
    const fetches = [
      api.get(`/divisions/${divId}`),
      api.get('/divisions?include_children=1').catch(() => ({ data: [] })),
      api.get(`/divisions/${divId}/updates`).catch(() => ({ data: [] })),
    ];
    Promise.all(fetches).then(async ([divRes, statsRes, updRes]) => {
      setDivision(divRes.data);
      const statsList = Array.isArray(statsRes.data) ? statsRes.data : [];
      const myStats = statsList.find(d => d.id === divId);
      setDivStats(myStats);
      setUpdates(updRes.data || []);

      if (divRes.data.has_contracts && !isScopedView) {
        try {
          const cRes = await api.get(`/contracts?division_id=${divId}`);
          setContracts(cRes.data);
        } catch {}
      }
      if (divRes.data.has_pipeline && !isScopedView) {
        // Pipeline now handled by PipelineModule component directly
        setPipeline([]);
        setPipelineStats(null);
      }
      // Fetch Clubs revenue for CC dashboard or ME B2B section (not for site-managers)
      if ((divId === 'cc-clubs' || divId === 'me') && !isScopedView) {
        try {
          const crRes = await api.get('/contracts/clubs-revenue');
          setClubsRevenue(crRes.data);
        } catch {}
      }
      // Fetch quarter stats (site-managers get site-filtered version)
      try {
        const siteParam = isScopedView && siteFilter ? `?site=${siteFilter}` : '';
        const qRes = await api.get(`/divisions/${divId}/quarter-stats${siteParam}`);
        setQuarterStats(qRes.data);
      } catch {}
      // Fetch outlets for chip selector
      try {
        const olRes = await api.get(`/outlets?division_id=${divId}`);
        setOutlets(olRes.data);
      } catch {}
      // Fetch performance scores for outlet chips
      try {
        const sRes = await api.get(`/outlet-intel/scores-batch?division_id=${divId}`);
        const scoreMap = {};
        (sRes.data.scores || []).forEach(s => { scoreMap[s.outlet] = s; });
        setOutletScores(scoreMap);
      } catch {}
    }).finally(() => setLoading(false));
  }, [divId, refreshTick]);

  // Re-fetch quarter stats when period or outlet changes
  useEffect(() => {
    if (!divId || loading) return;
    const params = new URLSearchParams();
    if (isScopedView && siteFilter) params.set('site', siteFilter);
    if (periodView !== 'quarterly') params.set('period', periodView);
    if (selectedOutlet !== 'all') params.set('outlet', selectedOutlet);
    // When viewing Monthly with a non-current month picked, pass the bounds
    // so the KPI cards reflect April / May / June (etc.) instead of "current".
    if (periodView === 'monthly' && selectedMonth) {
      const { from, to } = monthBoundaries(selectedMonth);
      params.set('month_from', from);
      params.set('month_to', to);
      params.set('month_ym', selectedMonth);
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    api.get(`/divisions/${divId}/quarter-stats${qs}`).then(r => setQuarterStats(r.data)).catch(() => {});
  }, [divId, periodView, selectedOutlet, selectedMonth, refreshTick]);

  const handleComment = async (updateId) => {
    if (!commentText.trim()) return;
    await api.post(`/updates/${updateId}/comment`, { text: commentText });
    setCommentText('');
    const updRes = await api.get(`/divisions/${divId}/updates`);
    setUpdates(updRes.data);
  };

  // HR-only accounts (Sneha / Nandini) live in the HR Hub — the division
  // dashboard (revenue, cost, pipeline) is outside their scope.
  if (isHrHomeUser(user)) return <Navigate to="/people" replace />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!division) return <p className="text-center py-10">Division not found</p>;

  const target = division.annual_target || 0;
  const ytd = divStats?.ytd_revenue || 0;
  const mtd = divStats?.mtd_revenue || 0;
  const contracted = divStats?.contracted_revenue || 0;
  const pipelineWt = divStats?.pipeline_weighted || 0;
  // Locked = money the division can count on this FY.
  // For contract divisions (CC/LC/CS), Contracted already INCLUDES rows
  // that have been realised — Realised is a subset, not an addition. So
  // Locked = Contracted (or Realised when there's no contract concept,
  // e.g. TDB daily sales).
  const totalLocked = Math.max(contracted, ytd);
  const pct = target ? ((totalLocked / target) * 100).toFixed(1) : 0;
  const healthColor = target ? (totalLocked / target >= 0.75 ? '#2D8B5E' : totalLocked / target >= 0.5 ? '#BA7517' : '#D4726A') : '#6B7280';

  // Contract-based divisions invoice clients monthly — no daily POS sales.
  // These get a "Monthly Billing" tab + monthly run rate instead of daily.
  const isContractDivision = ['lc', 'cc', 'cc-clubs', 'cc-ops', 'cs', 'cs-b2b', 'cs-b2c'].includes(divId);
  const salesTabLabel = isContractDivision ? 'Monthly Billing' : 'Daily Sales';
  const salesTabId = isContractDivision ? 'monthly-billing' : 'daily-sales';

  const tabs = isCostController
    ? [
        { id: 'cost', label: 'Cost' },
        { id: 'overview', label: 'Overview' },
        { id: 'analytics', label: 'Analytics' },
        { id: salesTabId, label: salesTabLabel },
        { id: 'people', label: 'People' },
        { id: 'updates', label: 'Weekly Updates' },
      ]
    : isScopedView
    ? [
        { id: 'overview', label: 'Overview' },
        { id: 'analytics', label: 'Analytics' },
        { id: salesTabId, label: salesTabLabel },
        { id: 'people', label: 'People' },
        { id: 'updates', label: 'Weekly Updates' },
      ]
    : (() => {
        // Contract divisions (LC/CC/CS) — Dipali approved 9-tab layout (2026-04-25):
        // Overview → Clients → Contracts → Pipeline → People → Training → P&L
        // → Mission → Weekly Updates.
        //
        // REMOVED for contract divisions:
        //   • Analytics — irrelevant (queries daily_sales which CC has none of)
        //   • Sales — duplicate of Pipeline at this level
        //   • Daily Sales — invoice-driven, not daily-billed
        if (isContractDivision) {
          return [
            { id: 'overview', label: 'Overview' },
            { id: 'clients', label: 'Clients' },
            ...(division.has_contracts ? [{ id: 'contracts', label: 'Contracts' }] : []),
            ...(division.has_pipeline ? [{ id: 'pipeline', label: 'Pipeline' }] : []),
            { id: 'bd', label: 'BD' },
            { id: 'people', label: 'People' },
            ...(['lc', 'cc', 'cc-clubs'].includes(divId) ? [{ id: 'training', label: 'Training' }] : []),
            { id: 'pnl', label: 'P&L' },
            { id: 'mission', label: 'Mission' },
            { id: 'legal', label: 'Legal' },
            { id: 'updates', label: 'Weekly Updates' },
          ];
        }
        // TDB + ME — existing order unchanged (daily-sales tab kept as-is)
        return [
          { id: 'overview', label: 'Overview' },
          { id: 'analytics', label: 'Analytics' },
          { id: 'pnl', label: 'P&L' },
          { id: salesTabId, label: salesTabLabel },
          ...(divId === 'me' ? [{ id: 'cost', label: 'Cost' }] : []),
          ...(divId === 'me' ? [{ id: 'vendors', label: 'Vendors' }] : []),
          ...(divId === 'me' ? [{ id: 'bookings', label: 'Bookings' }] : []),
          ...(divId === 'tdb' ? [{ id: 'tdb-cost', label: 'Cost' }] : []),
          ...(['tdb', 'me'].includes(divId) ? [{ id: 'marketing', label: 'Marketing' }] : []),
          { id: 'people', label: 'People' },
          ...(divId === 'me' ? [{ id: 'org-tree', label: 'Org Tree' }] : []),
          ...(['me', 'tdb'].includes(divId) ? [{ id: 'training', label: 'Training' }] : []),
          { id: 'clients', label: 'Clients' },
          ...(division.has_pipeline ? [{ id: 'pipeline', label: 'Pipeline' }] : []),
          ...(division.has_contracts ? [{ id: 'contracts', label: 'Contracts' }] : []),
          { id: 'mission', label: 'Mission' },
          { id: 'legal', label: 'Legal' },
          { id: 'updates', label: 'Weekly Updates' },
        ];
      })();

  return (
    <div className="space-y-4 fade-in" data-testid="division-detail">
      {/* Breadcrumb */}
      {isCeo && (
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 text-sm hover:opacity-70" style={{ color: '#2A7B88' }}
          data-testid="back-to-dashboard">
          <ChevronLeft size={16} /> Back to Dashboard
        </button>
      )}

      {/* === GAP SCREAMER (top of every division page — Dipali doctrine) === */}
      {(divStats?.annual_target || division?.annual_target) > 0 && (
        <DivisionGapScreamer
          divisionId={divId}
          divisionName={division?.name}
          target={divStats?.annual_target || division?.annual_target || 0}
          realised={divStats?.ytd_revenue || 0}
          leaderName={division?.leader_name}
        />
      )}

      {/* === TARGET PACE LIFEBAR (P1-a · 18-May-2026) ===
           Sits between Gap Screamer and Honest Status Strip per Dipali's spec.
           Trailing-7d pace + projected close + "push X/day extra" action line.
           For TDB this pulls from /api/tdb/leader-pulse (rich, real data);
           for other divisions falls back to /api/divisions/:divId/quarter-stats. */}
      <TargetPaceLifebar divisionId={divId} divisionName={division?.name} />

      {/* === HONEST STATUS STRIP (18-May-2026 doctrine fix) ===
           Surfaces the same revenue-first status_line + WoW + week-window
           context that CEO Dashboard tiles show — so a leader visiting
           their own page sees the same honest framing the CEO sees. */}
      {divStats && (divStats.status_line || divStats.wow_change || divStats.overdue_flag_text) && (
        <div className="rounded-[10px] px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          style={{ backgroundColor: '#F7F9FC', border: '0.5px solid #E5E7EB' }}
          data-testid="honest-status-strip">
          <div className="flex items-center gap-3 flex-wrap">
            {divStats.status_line && (
              <span className="text-[12px] font-medium" style={{ color: '#1B2A4A' }} data-testid="status-line-text">
                {divStats.status_line}
              </span>
            )}
            {divStats.wow_change !== undefined && divStats.wow_change !== 0 && (
              <span className="text-[12px] flex items-center gap-1" style={{ color: divStats.wow_change > 0 ? '#3B6D11' : '#A32D2D' }} data-testid="wow-change">
                <span>{divStats.wow_change > 0 ? '▲' : '▼'}</span>
                <span>{divStats.wow_change > 0 ? '+' : ''}{divStats.wow_change}% WoW</span>
                {divStats.week_window_label && (
                  <span className="text-[10px]" style={{ color: '#9CA3AF' }}>· {divStats.week_window_label}</span>
                )}
              </span>
            )}
          </div>
          {divStats.overdue_flag_text && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[8px] text-[11px] font-medium"
              style={{ backgroundColor: '#FCEBEB', color: '#A32D2D' }} data-testid="overdue-flag-badge">
              ⚠ {divStats.overdue_flag_text}
            </span>
          )}
        </div>
      )}

      {/* === WHAT'S NEW (recently shipped features — visible to every division) === */}
      <WhatsNewStrip />

      {/* === ROLE BANNER (non-CEO users) === */}
      {!isCeo && (
        <div className="flex items-center justify-between rounded-[10px] px-4 py-3" data-testid="role-banner"
          style={{ background: 'linear-gradient(90deg, #E6F1FB, #EAF3DE)', border: '0.5px solid #B5D4F4' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white" style={{ backgroundColor: '#185FA5' }}>
              {(user?.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <p className="text-[13px] font-medium" style={{ color: '#1B2A4A' }}>{user?.name}</p>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                {isSubLeader ? 'Sub-Leader' : isSiteManager ? 'Site Manager' : user?.role === 'submitter' ? 'Data Entry' : isCostController ? 'Cost Controller' : 'Division Leader'} · {division.name}
              </p>
            </div>
          </div>
          <div className="flex gap-1.5">
            {isSubLeader && user?.sub_view?.scope_value?.map(sv => (
              <span key={sv} className="text-[11px] px-2 py-1 rounded-lg" style={{ background: '#fff', border: '0.5px solid #B5D4F4', color: '#185FA5' }}>{sv}</span>
            ))}
            {isSiteManager && siteFilter && (
              <span className="text-[11px] px-2 py-1 rounded-lg" style={{ background: '#fff', border: '0.5px solid #B5D4F4', color: '#185FA5' }}>{siteFilter}</span>
            )}
          </div>
        </div>
      )}

      {/* === HEADER BAR === */}
      <div className="bg-white rounded-[10px] border px-5 py-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[15px] font-medium" style={{ color: '#1B2A4A' }} data-testid="division-name">{division.name}</h2>
              <DivisionDeltaChip divisionId={divId} />
              <button onClick={() => navigate('/guide')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-sm hover:shadow-md transition-shadow" style={{ background: 'linear-gradient(135deg, #2A7B88 0%, #1B2A4A 100%)' }} data-testid="quick-start-guide-chip-division">
                🧭 How do I…?
              </button>
            </div>
            <p className="text-xs" style={{ color: '#6B7280' }}>{division.leader_name} · {division.entity_name}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Period toggle pills */}
            <div className="flex gap-1">
              {['today', 'monthly', 'quarterly', 'ytd'].map(p => (
                <button key={p} onClick={() => setPeriodView(p)} data-testid={`period-${p}`}
                  className="px-3 py-1 rounded-full text-xs cursor-pointer transition-all"
                  style={{
                    background: periodView === p ? '#185FA5' : 'transparent',
                    color: periodView === p ? '#fff' : '#6B7280',
                    border: `0.5px solid ${periodView === p ? '#185FA5' : '#E5E1DC'}`,
                  }}>
                  {p === 'today' ? 'Today' : p === 'monthly' ? 'Monthly' : p === 'quarterly' ? 'Quarterly' : 'YTD'}
                </button>
              ))}
            </div>
            {/* Month picker — only when "Monthly" tab is active. Lets the
                CEO/leader scroll back to April / May / June / etc. and see
                that month's full channel matrix + per-city totals. */}
            {periodView === 'monthly' && (
              <MonthPicker
                value={selectedMonth}
                onChange={setSelectedMonth}
                earliestYM="2026-04"
                testId={`month-picker-${divId}`}
              />
            )}
            <button onClick={() => navigate(`/update/${divId}`)} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-white"
              style={{ backgroundColor: '#1B2A4A' }} data-testid="submit-update-btn">
              <FileEdit size={14} /> Submit Update
            </button>
          </div>
        </div>
      </div>

      {/* === KPI CARDS (Prototype style with accent bars + deltas) === */}
      {/* Sync status bar — shows last-synced timestamp + manual refresh
          button. Only for ME (where eZee live sync is wired). */}
      {divId === 'me' && (
        <SyncStatusBar divisionId={divId} />
      )}
      {/* Live ticker: renders only for revenue-active divisions (TDB + ME).
          In 'today' view → live polling snapshot with audit modal.
          In monthly/quarterly/YTD → aggregated period card sourced from
          quarterStats (no polling). Fixes the 19-May-2026 "values don't
          change when I toggle Today→Monthly" bug. */}
      {['tdb', 'me'].includes(divId) && (
        <LiveTicker
          divisionId={divId}
          periodView={periodView}
          periodLabel={periodView === 'monthly' ? selectedMonth : null}
          periodData={quarterStats}
        />
      )}

      {/* TDB-only · Missing-Days Ticker · sits directly above the
          City × Channel matrix so the leader sees which dates still
          need Mira/WhatsApp uploads BEFORE they inspect the matrix.
          Backend route: GET /api/tdb/missing-channels (returns the
          last 14 days × 3 cities × 5 manual channels). */}
      {divId === 'tdb' && (
        <TDBProdBootstrap user={user} className="mb-4" />
      )}

      {divId === 'tdb' && (
        <TDBMissingDaysTicker />
      )}

      {/* TDB-only · 60-Second Leader Pulse · health · operations ·
          accountability · 24h changelog · catalogue intel. Shipped
          18-May-2026 to make TDB the most intelligent division view. */}
      {divId === 'tdb' && (
        <TDBLeaderPulse />
      )}

      {/* TDB-only · Export Full MIS to Excel · one-click byte-for-byte
          v4-clone download honouring the active doctrine. CEO + TDB-
          leader gated server-side. Shipped 19-May-2026 · Sprint D. */}
      {divId === 'tdb' && (
        <TDBExportMIS className="mb-4" />
      )}

      {/* TDB-only · True P&L Breakdown · 6 buckets × 3 cities × Group
          from Mickky/Nihal's monthly MIS Excel (v4 layout supported).
          Shipped 18-May-2026 — Phase 1a of TDB MIS roadmap. */}
      {divId === 'tdb' && (
        <TDBPnLBreakdown />
      )}

      {/* TDB-only · Live MTD P&L · runs the doctrine engine on
          tdb_sales_daily + Shopify auto + mis_transactions in real
          time so Dipali sees the month-to-date P&L at any moment.
          Shipped 19-May-2026 · Sprint D. */}
      {divId === 'tdb' && (
        <TDBLiveMTDPnL className="mb-4" />
      )}

      {/* TDB-only · Reconciliation Drift Detector · diffs Mickky's MIS
          vs ScaleBoard MTD per city × per bucket. Default vs Source
          doctrine (matches Mickky's 1/3 split). Auto-runs daily
          08:30 IST during 1-Jun → 30-Jun parallel-run window.
          Shipped 19-May-2026 · Sprint D+. */}
      {divId === 'tdb' && (
        <TDBReconciliationDrift className="mb-4" />
      )}

      {/* TDB-only · Ingredient Master nav tile · Roji edits rates here,
          they cascade to every SKU instantly. CEO approves >20% changes.
          Shipped 20-May-2026. */}
      {divId === 'tdb' && (
        <TDBIngredientMasterTile className="mb-4" />
      )}

      {/* TDB-only · Recipe Editor nav tile · edit base / flavour /
          decoration recipes · rates from master · versioned.
          Shipped 20-May-2026. */}
      {divId === 'tdb' && (
        <TDBRecipeEditorTile className="mb-4" />
      )}

      {/* TDB-only · Bundle BOM nav tile · 750 hampers/combos with
          live cost computation from constituent components.
          Sprint E.5 · shipped 20-May-2026. */}
      {divId === 'tdb' && (
        <TDBBundleBOMTile className="mb-4" />
      )}

      {/* TDB-only · Sprint B · Britto's operational entry · daily sales
          grid (3 cities × 5 channels) + daily expense entry (37
          locked categories with audit log). Shipped 19-May-2026. */}
      {divId === 'tdb' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <DailySalesGrid />
          <DailyExpenseEntry />
        </div>
      )}

      {/* TDB-only · Sprint C · Roji's Stock Register (full-width) +
          Britto's Shopify inbox · Month-close gate checker · Phase 3
          primitives food cost. Shipped 19-May-2026. */}
      {divId === 'tdb' && (
        <>
          <TDBStockRegister className="mb-4" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <TDBShopifyUnallocated />
            <TDBMonthCloseCheck />
          </div>
        </>
      )}

      {/* TDB-only · User Manuals · Word .docx training material for
          Britto (Accountant) and Roji (Food Manager). Each role
          downloads only their own manual; CEO + Aditya get both.
          Shipped 20-May-2026. */}
      {divId === 'tdb' && (
        <TDBUserManuals className="mb-4" />
      )}

      {/* TDB-only · Product Boston Matrix · Kasavana-Smith / Stars ·
          Cash Cows · Puzzles · Dogs · using 41k+ Shopify orders.
          Y-axis auto-upgrades from price-tier to true contribution-
          per-unit once BOM coverage ≥ 20%. */}
      {divId === 'tdb' && (
        <TDBProductBostonMatrix />
      )}

      {/* TDB-only · Recipe Editor · Aditya/Nihal can view + add + edit
          + delete recipes (flavor or product). Saves to tdb_recipes
          which the Boston matrix consumes live — change a recipe,
          refresh the matrix, margins shift instantly. */}
      {divId === 'tdb' && (
        <TDBRecipesEditor />
      )}

      {/* TDB-only · Food Cost % blended + per-city KPI tile.
          Moved here from CEO dashboard 18-May-2026 — it's a TDB-specific
          drill-down, belongs inside /division/tdb not on /dashboard. */}
      {divId === 'tdb' && (
        <TDBFoodCostKPI />
      )}

      {/* City × Channel matrix
          • Today view: shows today's per-channel split (the live tile)
          • Monthly view: shows the picked month's full Apr/May/Jun matrix
          Only renders for divisions that have Shopify + manual channels
          (TDB primarily; ME once eZee live). */}
      {['tdb', 'me'].includes(divId) && periodView === 'today' && (
        <ChannelMatrix divisionId={divId} />
      )}
      {['tdb', 'me'].includes(divId) && periodView === 'monthly' && (() => {
        const { from, to } = monthBoundaries(selectedMonth);
        return <ChannelMatrix divisionId={divId} dateFrom={from} dateTo={to} />;
      })()}

      {target > 0 && (
        <div className={`grid grid-cols-2 sm:grid-cols-3 ${(divStats?.mg_ceiling || 0) > 0 ? 'md:grid-cols-7' : 'md:grid-cols-6'} gap-3`} data-testid="kpi-cards">
          {(() => {
            // Build tooltip data
            const targetBreakdown = divStats?.target_breakdown || [];
            const contractedBreakdown = divStats?.contracted_breakdown || [];
            const realisedBreakdown = divStats?.realised_breakdown || [];
            const pipelineBreakdown = divStats?.pipeline_breakdown || [];
            const hasB2B = targetBreakdown.some(t => t.is_b2b);

            let targetTooltip = [];
            if (hasB2B) {
              const opTotal = targetBreakdown.filter(t => !t.is_b2b).reduce((s, t) => s + t.value, 0);
              const b2bItems = targetBreakdown.filter(t => t.is_b2b);
              const b2bTotal = b2bItems.reduce((s, t) => s + t.value, 0);
              const b2bOwner = b2bItems[0]?.owner || '';
              targetTooltip = [
                { label: `Operating (${division?.leader_name || ''})`, value: opTotal },
                { label: `B2B Club Sales (${b2bOwner})`, value: b2bTotal },
              ];
            } else if (targetBreakdown.length > 1) {
              targetTooltip = targetBreakdown.map(t => ({
                label: `${t.label}${t.owner ? ` (${t.owner})` : ''}`,
                value: t.value,
              }));
            }

            const contractedTooltip = contractedBreakdown.map(c => ({
              label: c.name, value: c.value,
            }));

            const mgCeiling = divStats?.mg_ceiling || 0;
            const mgBreakdown = divStats?.mg_ceiling_breakdown || [];
            const mgTooltip = mgBreakdown.map(m => ({
              label: `${m.name} — ceiling ₹${(m.ceiling/1e7).toFixed(2)}Cr − fixed ₹${(m.fixed/1e7).toFixed(2)}Cr`,
              value: m.variable,
            }));

            const realisedTooltip = realisedBreakdown.map(r => ({
              label: `${r.name}${r.owner ? ` (${r.owner})` : ''}`, value: r.value,
            }));

            const pipelineTooltip = pipelineBreakdown.map(p => ({
              label: `${p.name} (${p.stage}, ${p.probability}%)`, value: p.value,
            }));

            const lockedTooltip = contracted > 0
              ? [
                  { label: 'Contracted (FY total)', value: contracted },
                  { label: 'of which already realised', value: ytd },
                  { label: 'Remaining to bill', value: Math.max(0, contracted - ytd) },
                ]
              : [
                  { label: 'Realised (actual)', value: ytd },
                ];

            // Pace calculation — Doctrine v2.3 (26-Apr-2026 fix):
            //   Show simple Q-PROGRESS as the headline number (Q-realised /
            //   Q-target). Previously we showed "pace ratio" — actual ÷ expected
            //   where expected = days_elapsed/days_in_period × target — which
            //   gave 245-247% on Day 25 for CS and confused everyone. The
            //   pace badge below still uses the ratio as a colour tag.
            //   Lump-sum advances (e.g. Dreamfolks ₹2.02Cr received Day 1 to
            //   cover all 12 months) artificially inflate the daily extrapolation,
            //   so the headline now sticks to the conservative
            //   "how much of THIS quarter's target have we collected" view.
            const qActual = quarterStats?.quarter_revenue;
            const qTarget = quarterStats?.quarter_target;
            const qDaysElapsed = quarterStats?.days_elapsed;
            const qDaysInPeriod = quarterStats?.days_in_period || (qDaysElapsed != null ? qDaysElapsed + (quarterStats?.days_remaining || 0) : null);
            const paceAvailable = qTarget > 0 && qDaysElapsed != null && qDaysInPeriod;
            // Q-PROGRESS = simple realised / target (the headline).
            const progressPct = paceAvailable ? Math.round((qActual / qTarget) * 100) : null;
            // PACE RATIO = actual / expected_by_now (kept for the colour tag).
            const paceExpected = paceAvailable ? (qDaysElapsed / qDaysInPeriod) * qTarget : 0;
            const pacePct = paceAvailable && paceExpected > 0 ? Math.round((qActual / paceExpected) * 100) : null;

            const kpis = [
              { label: 'Realised YTD', value: ytd, accent: '#378ADD',
                delta: divStats?.week_revenue > 0
                  ? `+${formatCurrency(divStats.week_revenue)} · ${divStats?.week_window_label || 'this week'}`
                  : null,
                deltaDir: 'up',
                tooltip: realisedTooltip.length > 0 ? realisedTooltip : null,
                tooltipTitle: 'Realised Breakdown (YTD)' },
              { label: 'Contracted', value: contracted, accent: '#2D8B5E', delta: null, tooltip: contractedTooltip.length > 0 ? contractedTooltip : null, tooltipTitle: 'Contracted Breakdown' },
              ...(mgCeiling > 0 ? [{
                label: 'MG Ceiling',
                value: mgCeiling,
                accent: '#7B5EA7',
                delta: 'variable, event-based',
                tooltip: mgTooltip.length > 0 ? mgTooltip : null,
                tooltipTitle: 'Variable MG Potential (above fixed commitments)'
              }] : []),
              { label: 'Pipeline (Wtd)', value: pipelineWt, accent: '#D97706', delta: null, tooltip: pipelineTooltip.length > 0 ? pipelineTooltip : null, tooltipTitle: 'Pipeline Breakdown (Weighted)' },
              { label: 'Locked', value: totalLocked, accent: healthColor, delta: `${pct}% of target`, deltaDir: parseFloat(pct) >= 40 ? 'up' : 'down', tooltip: (ytd > 0 || contracted > 0) ? lockedTooltip : null, tooltipTitle: 'Locked Breakdown' },
              {
                label: 'Target',
                value: target,
                accent: '#1B2A4A',
                delta: null,
                tooltip: targetTooltip.length > 0 ? targetTooltip : null,
                tooltipTitle: 'Target Breakdown',
                customNode: (
                  <div>
                    <p className="text-[22px] font-medium" style={{ color: '#1B2A4A' }}>
                      <EditableTarget
                        value={target}
                        entityType="division"
                        entityId={division._id || division.id}
                        label={division.name}
                        onSaved={refreshAll}
                        testId={`division-target-edit-${division._id || division.id}`}
                        formatValue={formatCurrency}
                      />
                    </p>
                  </div>
                ),
              },
              {
                label: isContractDivision ? 'Q-Progress (Invoiced)' : 'Q-Progress',
                customNode: paceAvailable ? (
                  <div>
                    <p className="text-[22px] font-medium" style={{ color: '#1B2A4A' }} data-testid="kpi-progress-pct">
                      {progressPct != null ? `${progressPct}%` : '—'}
                    </p>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <PaceIndicator actual={qActual} target={qTarget} daysElapsed={qDaysElapsed} daysInPeriod={qDaysInPeriod} size="sm" showPct={false} invoicedLabel={isContractDivision} />
                      <span className="text-[10px]" style={{ color: '#9CA3AF' }} title={`Pace ratio = realised ÷ (expected by day ${qDaysElapsed}/${qDaysInPeriod}) — useful when revenue is roughly even across the quarter, less meaningful when payments are lumpy or upfront`}>
                        pace {pacePct}%
                      </span>
                    </div>
                    <p className="text-[10px] mt-1.5" style={{ color: '#6B7280' }}
                       title={`Realised ${formatCurrency(qActual || 0)} of ${formatCurrency(qTarget || 0)} ${quarterStats?.quarter_label || 'Q'} target. Day ${qDaysElapsed} of ${qDaysInPeriod}.`}>
                      {isContractDivision
                        ? `${Math.round((Math.max(contracted, ytd) / (target || 1)) * 100)}% locked vs FY target`
                        : `${formatCurrency(qActual || 0)} of ${formatCurrency(qTarget || 0)}`}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-[22px] font-medium" style={{ color: '#9CA3AF' }}>—</p>
                    <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>No target set</p>
                  </div>
                ),
                accent: progressPct == null ? '#9CA3AF' : progressPct >= 100 ? '#14532D' : progressPct >= 75 ? '#2D8B5E' : progressPct >= 50 ? '#BA7517' : '#D4726A',
              },
            ];

            return kpis.map((kpi, i) => (
              <div key={i} className="bg-white rounded-[10px] border p-3.5 relative group cursor-default" style={{ borderColor: '#E5E1DC' }}
                data-testid={`kpi-card-${kpi.label.toLowerCase().replace(/[\s()\/+]/g, '-')}`}>
                <div className="w-7 h-[3px] rounded-sm mb-2.5" style={{ backgroundColor: kpi.accent }} />
                <p className="text-[11px] mb-1.5" style={{ color: '#6B7280' }}>{kpi.label}</p>
                {kpi.customNode ? kpi.customNode : (
                  <>
                    <p className="text-[22px] font-medium" style={{ color: '#1B2A4A' }}>{formatCurrency(kpi.value)}</p>
                    {kpi.delta && (
                      <p className="text-[11px] mt-1" style={{ color: kpi.deltaDir === 'up' ? '#3B6D11' : '#A32D2D' }}>
                        {kpi.deltaDir === 'up' ? '▲' : '▼'} {kpi.delta}
                      </p>
                    )}
                  </>
                )}
                {kpi.tooltip && !kpi.customNode && (
                  <div className={`absolute top-full mt-2 hidden group-hover:block z-50 bg-[#1B2A4A] text-white rounded-lg p-3 shadow-xl ${i >= 3 ? 'right-0' : 'left-0'}`}
                    style={{ minWidth: '260px', maxWidth: '340px' }} data-testid={`tooltip-${kpi.label.toLowerCase().replace(/[\s()\/+]/g, '-')}`}>
                    <div className={`absolute bottom-full ${i >= 3 ? 'right-4' : 'left-4'} w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-[#1B2A4A]`} />
                    <p className="text-[10px] font-semibold mb-2 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{kpi.tooltipTitle}</p>
                    <div className="max-h-[200px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                      {kpi.tooltip.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-[11px] mb-1.5 gap-4">
                          <span style={{ color: 'rgba(255,255,255,0.75)' }}>{item.label}</span>
                          <span className="font-medium whitespace-nowrap">{formatCurrency(item.value)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-white/20 mt-2 pt-2 flex justify-between text-[11px]">
                      <span className="font-semibold">Total</span>
                      <span className="font-semibold">{formatCurrency(kpi.value)}</span>
                    </div>
                  </div>
                )}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Progress bar */}
      {target > 0 && (
        <div className="bg-white rounded-[10px] border px-4 py-3" style={{ borderColor: '#E5E1DC' }}>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
            <div className="h-full rounded-full flex">
              {ytd > 0 && <div style={{ width: `${Math.min(ytd / target * 100, 100)}%`, backgroundColor: '#2D8B5E' }} />}
              {contracted > 0 && <div style={{ width: `${Math.min(contracted / target * 100, 100 - (ytd / target * 100))}%`, backgroundColor: '#378ADD' }} />}
            </div>
          </div>
          <div className="flex gap-4 mt-1.5 text-[10px]" style={{ color: '#6B7280' }}>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#2D8B5E' }} />Realised</span>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#378ADD' }} />Contracted</span>
            <span style={{ color: healthColor }}>{pct}% locked</span>
          </div>
        </div>
      )}

      {/* === REALISED REVENUE LEDGER (CRUD for b2b_revenue entries) === */}
      <DivisionRealisedLedger divisionId={divId} />

      {/* === OUTLET CHIP SELECTOR === */}
      {outlets.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="outlet-chips">
          <button onClick={() => setSelectedOutlet('all')}
            className="px-3 py-1 rounded-2xl text-xs cursor-pointer transition-all"
            style={{
              background: selectedOutlet === 'all' ? '#185FA5' : '#fff',
              color: selectedOutlet === 'all' ? '#fff' : '#6B7280',
              border: `0.5px solid ${selectedOutlet === 'all' ? '#185FA5' : '#E5E1DC'}`,
            }} data-testid="outlet-chip-all">
            All
          </button>          {outlets
            .filter(o => o.outlet_type !== 'training_library' && o.outlet_type !== 'platform')
            .map(o => {
            const isComingSoon = o.status === 'coming_soon' || o.status === 'pre_scale';
            const score = outletScores[o.name];
            const nameLower = (o.name || '').toLowerCase();
            const isPipelineBucket = nameLower === 'new business pipeline' || o.revenue_source === 'pipeline_contracts';
            const isCostCentre = Boolean(
              o.is_cost_centre ||
              o.outlet_type === 'cost_centre' ||
              score?.is_cost_centre ||
              score?.health === 'cost_centre' ||
              nameLower.includes('common area') ||
              nameLower.startsWith('hq ') ||
              nameLower === 'hq'
            );
            const healthColor = score?.health === 'green' ? '#2D8B5E' : score?.health === 'amber' ? '#D97706' : score?.health === 'red' ? '#DC2626' : null;
            return (
              <div key={o.id} className="flex items-center gap-0">
                <button
                  onClick={() => {
                    if (isComingSoon) return;
                    // Cost centres: nothing useful to filter — open full drill-down instead
                    if (isCostCentre) {
                      navigate(`/division/${divId}/outlet/${encodeURIComponent(o.name)}`);
                      return;
                    }
                    setSelectedOutlet(o.name);
                  }}
                  className={`px-3 py-1.5 rounded-l-2xl text-sm transition-all flex items-center gap-2 ${isComingSoon ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
                  style={{
                    background: !isComingSoon && selectedOutlet === o.name ? '#185FA5' : '#fff',
                    color: !isComingSoon && selectedOutlet === o.name ? '#fff' : isComingSoon ? '#B0B0B0' : '#6B7280',
                    border: `0.5px solid ${!isComingSoon && selectedOutlet === o.name ? '#185FA5' : '#E5E1DC'}`,
                    borderRight: 'none',
                  }} data-testid={`outlet-chip-${o.id}`}
                  title={
                    isCostCentre
                      ? 'Cost centre — click to open drill-down (People / Assets / Compliance / Cost Summary)'
                      : (score?.score !== null && score?.score !== undefined
                        ? `Health: ${score.score}/10${score.rev_pct != null ? ` · Revenue pace ${score.rev_pct}% of target` : ''}${score.dimensions_measured != null ? ` · ${score.dimensions_measured}/${6} dimensions measured` : ''}`
                        : 'Insufficient data for scoring')}>
                  {o.name}{isComingSoon ? ' (soon)' : ''}
                  {isPipelineBucket && !isComingSoon && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: selectedOutlet === o.name ? '#ffffff' : '#FEF3C7', color: '#BA7517' }}
                      title="Pipeline bucket — auto-aggregates deals not yet attached to a dedicated client. Add a contract in the Client Dossier to move a deal out of this bucket."
                      data-testid={`pipeline-bucket-${o.id}`}
                    >
                      Pipeline
                    </span>
                  )}
                  {isCostCentre && !isComingSoon && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: selectedOutlet === o.name ? '#ffffff' : '#185FA520', color: selectedOutlet === o.name ? '#185FA5' : '#185FA5' }} data-testid={`cost-centre-${o.id}`}>
                      Cost
                    </span>
                  )}
                  {!isCostCentre && healthColor && !isComingSoon && score?.score !== null && (
                    <span className="inline-flex items-center justify-center text-[11px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: healthColor + '25', color: healthColor, minWidth: '28px' }} data-testid={`score-${o.id}`}>
                      {score.score}
                      {score.dimensions_measured != null && score.dimensions_measured < 6 && (
                        <sup className="text-[7px] ml-0.5 opacity-75">{score.dimensions_measured}d</sup>
                      )}
                    </span>
                  )}
                  {!isCostCentre && !isComingSoon && score?.health === 'insufficient_data' && (
                    <span className="inline-flex items-center justify-center text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#9CA3AF20', color: '#6B7280' }} title="Insufficient data for scoring">—</span>
                  )}
                </button>
                {!isComingSoon && (
                  <OutletLink name={o.name} divId={divId}>
                    <span className="px-1.5 py-1 rounded-r-2xl text-[10px] font-bold transition-all hover:bg-[#2A7B88] hover:text-white" style={{
                      background: selectedOutlet === o.name ? '#0e5f6b' : '#F7F5F2',
                      color: selectedOutlet === o.name ? '#fff' : '#2A7B88',
                      border: `0.5px solid ${selectedOutlet === o.name ? '#185FA5' : '#E5E1DC'}`,
                    }} title={`View ${o.name} details`}>&#8599;</span>
                  </OutletLink>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Quarter/Period Progress — context-aware */}
      {quarterStats && quarterStats.quarter_target > 0 && (
        <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="quarter-stats">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                {quarterStats.period_label || quarterStats.quarter_label} Progress
                {selectedOutlet !== 'all' && (
                  <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded-lg font-medium" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>
                    {selectedOutlet.replace(/MindEscapes |— /g, '')}
                  </span>
                )}
              </h4>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                Day {quarterStats.days_elapsed} of {quarterStats.days_in_period || (quarterStats.days_elapsed + quarterStats.days_remaining)} &middot; {quarterStats.days_remaining} days remaining
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold" style={{ color: quarterStats.quarter_pct >= 20 ? '#2D8B5E' : quarterStats.quarter_pct >= 0 ? '#BA7517' : '#D4726A' }}>
                {periodView === 'today' ? `${quarterStats.quarter_pct > 0 ? '+' : ''}${quarterStats.quarter_pct}%` : `${quarterStats.quarter_pct}%`}
              </p>
              <p className="text-xs" style={{ color: '#6B7280' }}>{periodView === 'today' ? 'vs yesterday' : `of ${quarterStats.period_label || quarterStats.quarter_label} target`}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="p-2 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
              <p className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>{periodView === 'today' ? 'Today' : periodView === 'monthly' ? 'Month' : periodView === 'ytd' ? 'YTD' : (quarterStats.period_label || quarterStats.quarter_label)} Actual</p>
              <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{formatCurrency(quarterStats.quarter_revenue)}</p>
            </div>
            <div className="p-2 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
              <p className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>{periodView === 'today' ? 'Yesterday' : periodView === 'monthly' ? 'Month' : periodView === 'ytd' ? 'Annual' : (quarterStats.period_label || quarterStats.quarter_label)} Target</p>
              <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{formatCurrency(quarterStats.quarter_target)}</p>
              {isContractDivision && (quarterStats.quarter_target || 0) > 0 && (
                <p className="text-[10px] mt-0.5" style={{ color: '#059669' }}>
                  Remaining: {formatCurrency(Math.max(0, (quarterStats.quarter_target || 0) - (quarterStats.quarter_revenue || 0)))}
                </p>
              )}
            </div>
            {isContractDivision ? (() => {
              // Monthly billing run rate — remaining target ÷ remaining months (of the quarter/year being shown)
              const remaining = Math.max(0, (quarterStats.quarter_target || 0) - (quarterStats.quarter_revenue || 0));
              // daily_run_rate_needed exists on the response; multiply by ~30 to get a monthly figure without a backend change.
              // But to be strictly correct, derive months remaining from days remaining in the period.
              const daysIn = quarterStats.days_in_period || 91;
              const daysElapsed = quarterStats.days_elapsed || 0;
              const daysRemaining = Math.max(1, daysIn - daysElapsed);
              const monthsRemaining = Math.max(0.1, +(daysRemaining / 30.4).toFixed(1));
              const monthlyNeeded = remaining / monthsRemaining;
              return (
                <>
                  <div className="p-2 rounded-md col-span-2" style={{ backgroundColor: '#FEF3C7' }} data-testid="monthly-billing-needed">
                    <p className="text-[10px] font-medium uppercase" style={{ color: '#92400E' }}>Monthly Billing Needed</p>
                    <p className="text-sm font-bold" style={{ color: '#92400E' }}>{formatCurrency(monthlyNeeded)}/month</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#92400E' }}>
                      {monthsRemaining} months remaining in {quarterStats.period_label || quarterStats.quarter_label}
                    </p>
                  </div>
                </>
              );
            })() : periodView === 'today' ? (
              <>
                <div className="p-2 rounded-md" style={{ backgroundColor: '#ECFDF5' }} data-testid="today-delta">
                  <p className="text-[10px] font-medium uppercase" style={{ color: '#065F46' }}>Delta vs Yesterday</p>
                  <p className="text-sm font-bold" style={{ color: quarterStats.quarter_pct >= 0 ? '#065F46' : '#B91C1C' }}>
                    {formatCurrency(Math.abs((quarterStats.quarter_revenue || 0) - (quarterStats.quarter_target || 0)))}
                    {quarterStats.quarter_pct >= 0 ? ' ▲' : ' ▼'}
                  </p>
                </div>
                <div className="p-2 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
                  <p className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>Data Source</p>
                  <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Live</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>polled 30s</p>
                </div>
              </>
            ) : (
              <>
                <div className="p-2 rounded-md" style={{ backgroundColor: '#FEF3C7' }}>
                  <p className="text-[10px] font-medium uppercase" style={{ color: '#92400E' }}>Daily Run Rate Needed</p>
                  <p className="text-sm font-bold" style={{ color: '#92400E' }}>{formatCurrency(quarterStats.daily_run_rate_needed)}/day</p>
                </div>
                <div className="p-2 rounded-md" style={{ backgroundColor: '#FEF3C7' }}>
                  <p className="text-[10px] font-medium uppercase" style={{ color: '#92400E' }}>Weekly Run Rate Needed</p>
                  <p className="text-sm font-bold" style={{ color: '#92400E' }}>{formatCurrency(quarterStats.weekly_run_rate_needed)}/wk</p>
                </div>
              </>
            )}
          </div>
          {/* Outlet breakdown for ME */}
          {quarterStats.outlet_breakdown?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: '#F7F5F2' }}>
                    <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>{isContractDivision ? 'Client' : 'Outlet'}</th>
                    <th className="text-left px-3 py-2 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Owner</th>
                    <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>{periodView === 'today' ? 'Today' : periodView === 'monthly' ? 'Month' : periodView === 'ytd' ? 'YTD' : (quarterStats.period_label || quarterStats.quarter_label)} Actual</th>
                    <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>{periodView === 'today' ? 'Yesterday' : periodView === 'monthly' ? 'Month' : periodView === 'ytd' ? 'Annual' : (quarterStats.period_label || quarterStats.quarter_label)} Target</th>
                    {isContractDivision && periodView !== 'today' && (
                      <th className="text-right px-3 py-2 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>Remaining</th>
                    )}
                    <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>{periodView === 'today' ? 'Δ%' : '%'}</th>
                    {periodView !== 'today' && (
                      <th className="text-center px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Pace</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                  {quarterStats.outlet_breakdown.map((o, i) => {
                    const isNewBizGap = o.is_new_business_gap === true;
                    return (
                    <tr key={i} style={isNewBizGap ? { backgroundColor: '#FEF3C7' } : {}}>
                      <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>
                        {isNewBizGap ? (
                          <span className="flex items-center gap-1.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase" style={{ backgroundColor: '#BA7517', color: '#FFFFFF' }}>Gap</span>
                            <span title={`Flexible: ₹${(o.annual_target/1e7).toFixed(2)}Cr to be signed AND realised by 31 Mar 2027. No fixed quarterly split.`}>{o.label}</span>
                          </span>
                        ) : (
                          <OutletLink name={o.label} divId={divId} className="text-[#2A7B88] underline decoration-[#2A7B88]/30 hover:decoration-[#2A7B88]">{o.label}</OutletLink>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell text-xs" style={{ color: '#6B7280' }}>{o.owner || '—'}</td>
                      <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{formatCurrency(o.actual)}</td>
                      <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>
                        {isNewBizGap ? (
                          <span title="Not apportioned per quarter — collect any time before 31 Mar 2027">
                            <span className="text-[10px]" style={{ color: '#BA7517' }}>Annual: {formatCurrency(o.annual_target)}</span>
                          </span>
                        ) : (
                          formatCurrency(o.target)
                        )}
                      </td>
                      {isContractDivision && periodView !== 'today' && (
                        <td className="px-3 py-2 text-right hidden md:table-cell" style={{ color: isNewBizGap ? '#BA7517' : '#059669', fontWeight: 500 }}>
                          {isNewBizGap ? (
                            <span title="₹2Cr total still to sign + realise"><span className="text-[10px]">{formatCurrency(o.annual_target)}</span></span>
                          ) : (
                            <span title={`Remaining to collect in ${quarterStats.period_label || 'Q'}`}>{formatCurrency(Math.max(0, (o.target || 0) - (o.actual || 0)))}</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2 text-right">
                        {isNewBizGap ? (
                          <span className="text-[10px]" style={{ color: '#9CA3AF' }}>—</span>
                        ) : (
                          <span className="text-xs font-semibold" style={{
                            color: periodView === 'today'
                              ? (o.pct > 0 ? '#2D8B5E' : o.pct === 0 ? '#6B7280' : '#D4726A')
                              : (o.pct >= 20 ? '#2D8B5E' : o.pct > 0 ? '#BA7517' : '#D4726A')
                          }}>{periodView === 'today' && o.pct > 0 ? `+${o.pct}%` : `${o.pct}%`}</span>
                        )}
                      </td>
                      {periodView !== 'today' && (
                        <td className="px-3 py-2 text-center">
                          {isNewBizGap ? (
                            <span className="text-[10px]" style={{ color: '#9CA3AF' }}>flexible</span>
                          ) : o.first_payment_date && new Date(o.first_payment_date) > new Date() ? (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                              style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}
                              title={`First invoice scheduled for ${o.first_payment_date}. Pace measurement starts after this date.`}
                            >
                              Starts {new Date(o.first_payment_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          ) : o.target > 0 ? (
                            <PaceIndicator
                              actual={o.actual || 0}
                              target={o.target}
                              daysElapsed={quarterStats.days_elapsed}
                              daysInPeriod={quarterStats.days_in_period || (quarterStats.days_elapsed + quarterStats.days_remaining)}
                              size="sm"
                              showStatus={false}
                            />
                          ) : (
                            <span className="text-[10px]" style={{ color: '#9CA3AF' }}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Clubs Revenue (lands in ME) — shown on Nisha's CC dashboard */}
      {divId === 'cc-clubs' && clubsRevenue && clubsRevenue.contracts?.length > 0 && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC', borderLeftWidth: '4px', borderLeftColor: '#2D8B5E' }}
          data-testid="clubs-revenue-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Clubs Revenue</h4>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
                  Lands in ME P&L
                </span>
              </div>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                Nisha drives these deals — revenue reports under MindEscapes, not CC.
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase" style={{ color: '#6B7280' }}>Signed Value</p>
              <p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{formatCurrency(clubsRevenue.signed_total)}</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }}>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Client</th>
                  <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Value</th>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Type</th>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {clubsRevenue.contracts.map((c, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{c.client_name}</td>
                    <td className="px-3 py-2 text-right" style={{ color: c.contract_type === 'signed' ? '#2D8B5E' : '#6B7280' }}>
                      {c.annual_value ? formatCurrency(c.annual_value) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        c.contract_type === 'signed' ? 'bg-green-50 text-green-700' :
                        c.contract_type === 'projected' ? 'bg-amber-50 text-amber-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {c.contract_type || c.renewal_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{c.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* B2B Club Sales — shown on ME dashboard (Nisha drives, Dhiraj plans delivery) */}
      {divId === 'me' && clubsRevenue && clubsRevenue.contracts?.length > 0 && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC', borderLeftWidth: '4px', borderLeftColor: '#3B5BA9' }}
          data-testid="b2b-club-sales-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>B2B Club Sales</h4>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#EFF6FF', color: '#3B5BA9' }}>
                  Nisha drives
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
                  Not operating revenue
                </span>
              </div>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                Contracted B2B that Nisha books. Shown here for delivery planning — does not count as Dhiraj&apos;s operating revenue.
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase" style={{ color: '#6B7280' }}>Signed</p>
              <p className="text-lg font-bold" style={{ color: '#3B5BA9' }}>{formatCurrency(clubsRevenue.signed_total)}</p>
              {clubsRevenue.projected_total > 0 && (
                <p className="text-xs" style={{ color: '#BA7517' }}>+{formatCurrency(clubsRevenue.projected_total)} projected</p>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }}>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Deal</th>
                  <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Value</th>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Details</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {clubsRevenue.contracts.map((c, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{c.client_name}</td>
                    <td className="px-3 py-2 text-right" style={{ color: c.contract_type === 'signed' ? '#2D8B5E' : '#6B7280' }}>
                      {c.annual_value ? formatCurrency(c.annual_value) : 'TBD'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        c.contract_type === 'signed' ? 'bg-green-50 text-green-700' :
                        c.contract_type === 'projected' ? 'bg-amber-50 text-amber-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {c.contract_type || 'active'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{c.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weekly Reflection Card */}
      <ReflectionCard divisionId={divId} />

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.id === 'tdb-cost') {
                navigate('/tdb/cost');
              } else {
                setActiveTab(tab.id);
              }
            }}
            data-testid={`tab-${tab.id}`}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id ? 'border-[#2A7B88]' : 'border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? '#1B2A4A' : '#6B7280' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4" data-testid="overview-tab">
          {/* Quick-action shortcuts for the leader (Overview-tab only) */}
          {(user?.role === 'leader' || user?.role === 'ceo') && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="overview-quick-actions">
              {division.has_pipeline && (
                <button
                  onClick={() => { setActiveTab('pipeline'); navigate(`/division/${divId}?tab=pipeline&addDeal=1`, { replace: true }); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-white shadow-sm hover:shadow-md transition-shadow"
                  style={{ background: 'linear-gradient(135deg,#2A7B88,#1B2A4A)' }}
                  data-testid="overview-add-deal-shortcut">
                  + New Deal
                </button>
              )}
              {division.has_daily_sales && (
                <button
                  onClick={() => setActiveTab('daily-sales')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border"
                  style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                  data-testid="overview-add-sales-shortcut">
                  + Daily Sales
                </button>
              )}
              {division.has_b2b && (
                <button
                  onClick={() => setActiveTab('b2b')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border"
                  style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                  data-testid="overview-add-b2b-shortcut">
                  + B2B Revenue
                </button>
              )}
              <button
                onClick={() => setActiveTab('people')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border"
                style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                data-testid="overview-add-person-shortcut">
                + Person
              </button>
              {divId === 'me' && (
                <button
                  onClick={() => setActiveTab('vendors')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border"
                  style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                  data-testid="overview-add-vendor-shortcut">
                  + Vendor
                </button>
              )}
              <button
                onClick={() => navigate('/sops?new=1')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border"
                style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                data-testid="overview-add-sop-shortcut"
                title="Add a new SOP / training module">
                + SOP
              </button>
              <button
                onClick={() => {
                  const subject = encodeURIComponent(`Issue on ScaleBoard · ${division.name}`);
                  const body = encodeURIComponent(`Hi Dipali,\n\nI'm hitting an issue on the ${division.name} page:\n\n[describe the issue + what you were trying to do]\n\nBrowser: ${navigator.userAgent}\nURL: ${window.location.href}\n\n— ${user?.name || 'Leader'}`);
                  window.open(`mailto:dipali@clubconcierge.in?subject=${subject}&body=${body}`, '_blank');
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border"
                style={{ borderColor: '#DC2626', color: '#DC2626' }}
                data-testid="overview-report-issue-shortcut"
                title="Report an issue to Dipali">
                Report an Issue
              </button>
              <button
                onClick={() => navigate('/mira')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border ml-auto"
                style={{ borderColor: '#D4726A', color: '#D4726A' }}
                data-testid="overview-ask-mira-shortcut"
                title="Ask Mira to enter data for you — works on WhatsApp too">
                Ask Mira
              </button>
            </div>
          )}
          {/* My Briefcase — Friday report + briefing + anomalies, per leader */}
          {!isCeo && <BriefcaseTile user={user} />}
          {/* Revenue Trend (history) — shows the 3-year story and flags decline */}
          <RevenueTrendWidget divisionId={divId} divisionName={division.name} isCEO={user?.role === 'ceo'} />

          {/* Latest Metrics */}
          {divStats?.latest_quantitative && Object.keys(divStats.latest_quantitative).length > 0 && (
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <div className="flex items-baseline justify-between mb-3">
                <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Latest Metrics</h4>
                {divStats.latest_metrics_source === 'ezee_live_mtd' && (
                  <span
                    data-testid="latest-metrics-source-badge"
                    title="MTD revenue per outlet, computed live from eZee receipts (net_sales)"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: '#10B98120', color: '#059669', border: '1px solid #10B98140' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live · MTD
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {division.quantitative_fields?.map(field => {
                  const val = divStats.latest_quantitative[field.key];
                  if (val === undefined || val === null || val === '') return null;
                  const displayVal = field.type === 'currency' ? `${formatCurrency(val)}` :
                    field.type === 'percentage' ? `${val}%` : val;
                  return (
                    <div key={field.key} className="p-2 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
                      <p className="text-xs" style={{ color: '#6B7280' }}>{field.label}</p>
                      <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{displayVal}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Latest Qualitative */}
          {divStats?.latest_qualitative && Object.keys(divStats.latest_qualitative).length > 0 && (
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>Latest Qualitative Update</h4>
              <div className="space-y-2">
                {division.qualitative_fields?.map(field => {
                  const val = divStats.latest_qualitative[field.key];
                  if (!val) return null;
                  return (
                    <div key={field.key}>
                      <p className="text-xs font-medium" style={{ color: '#6B7280' }}>{field.label}</p>
                      <p className="text-sm" style={{ color: '#1B2A4A' }}>{typeof val === 'boolean' ? (val ? 'Yes' : 'No') : val}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Wins & Alerts + Cost Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <WinsAndMomentum divisionId={divId} />
            {divId === 'me' && <CostSummaryWidget />}
            <PeopleWarnings divisionId={divId} />
          </div>
          {['tdb', 'me'].includes(divId) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="overview-analytics-summary">
              <MiniRevenueTrend divisionId={divId} />
              <MiniContribution divisionId={divId} />
              <MiniLeaderboard divisionId={divId} limit={5} />
            </div>
          )}
          {divId === 'me' && <StaysIntelTile />}
          {divId === 'me' && <MonthlyArchive />}
        </div>
      )}

      {activeTab === 'analytics' && (
        <AnalyticsTab divisionId={divId} divisionName={division.name} />
      )}

      {activeTab === 'pnl' && (
        <DivisionPnL divisionId={divId} />
      )}

      {activeTab === 'pipeline' && division.has_pipeline && (
        <PipelineModule divisionId={divId} divisionName={division.name}
          autoOpenAddDeal={new URLSearchParams(window.location.search).get('addDeal') === '1'} />
      )}

      {activeTab === 'bd' && (
        <BDModule divisionId={divId} divisionName={division.name} />
      )}

      {activeTab === 'marketing' && divId === 'tdb' && (
        <div className="space-y-4">
          <UnattributedOrdersCard divisionId="tdb" sinceDays={30} />
          <MarketingModule divisionId={divId} divisionName={division.name} />
        </div>
      )}

      {activeTab === 'marketing' && divId === 'me' && (
        <MEMarketingModule divisionId={divId} divisionName={division.name} />
      )}

      {activeTab === 'sales' && ['lc', 'cc-clubs', 'cc-ops', 'cs'].includes(divId) && (
        <SalesModule divisionId={divId} divisionName={division.name} />
      )}

      {activeTab === 'contracts' && division.has_contracts && (
        <ContractTable contracts={contracts} onRefresh={async () => {
          const cRes = await api.get(`/contracts?division_id=${divId}`);
          setContracts(cRes.data);
        }} divisionId={divId} />
      )}

      {activeTab === 'daily-sales' && (
        <DailySalesTab divisionId={divId} />
      )}

      {activeTab === 'monthly-billing' && (
        <div className="space-y-4">
          <InvoiceSLATracker divisionId={divId} thresholdDays={30} />
          <MonthlyBillingTab divisionId={divId} onChange={() => {}} />
        </div>
      )}

      {activeTab === 'bookings' && divId === 'me' && (
        <BookingPipeline />
      )}

      {activeTab === 'cost' && divId === 'me' && (
        <div className="space-y-5">
          <GroupFoodCostTile />
          <CostModule />
        </div>
      )}

      {activeTab === 'vendors' && divId === 'me' && (
        <VendorModule />
      )}

      {activeTab === 'people' && (
        <PeopleTab divisionId={divId} divisionName={division.name} />
      )}

      {activeTab === 'org-tree' && (
        <div className="space-y-3" data-testid="org-tree-tab">
          <div>
            <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>Reporting Tree — {division.name}</h3>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Drag a person onto a new manager to reassign. Click any name to open their profile.</p>
          </div>
          <ReportingTree
            divisionId={divId}
            canEdit={user?.role === 'ceo' || user?.role === 'leader'}
            onPersonClick={(n) => navigate(`/people/${n.id}`)}
          />
        </div>
      )}

      {activeTab === 'training' && (
        <DivisionTrainingCenter divisionId={divId} isCEO={user?.role === 'ceo'} />
      )}

      {activeTab === 'clients' && (
        <ClientTab divisionId={divId} />
      )}

      {activeTab === 'mission' && (
        <MissionTab divisionId={divId} />
      )}

      {activeTab === 'legal' && (
        <LegalComplianceTab divisionId={divId} />
      )}

      {activeTab === 'updates' && (
        <div className="space-y-4" data-testid="updates-tab">
          {updates.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-sm" style={{ color: '#6B7280' }}>No updates submitted yet.</p>
            </div>
          ) : (
            updates.map((upd, idx) => (
              <div key={upd.id} className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}
                data-testid={`update-${upd.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Week of {upd.week_start}</p>
                    <p className="text-xs" style={{ color: '#9CA3AF' }}>
                      Submitted {upd.submitted_at ? new Date(upd.submitted_at).toLocaleString('en-IN') : ''}
                    </p>
                  </div>
                </div>

                {/* Quantitative */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {Object.entries(upd.quantitative || {}).map(([key, val]) => {
                    const field = division.quantitative_fields?.find(f => f.key === key);
                    if (!field || val === '' || val === null || val === undefined) return null;
                    const displayVal = field.type === 'currency' ? formatCurrency(val) :
                      field.type === 'percentage' ? `${val}%` : val;
                    return (
                      <div key={key} className="text-xs p-1.5 rounded" style={{ backgroundColor: '#F7F5F2' }}>
                        <span style={{ color: '#6B7280' }}>{field.label}: </span>
                        <span className="font-medium" style={{ color: '#1B2A4A' }}>{displayVal}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Qualitative */}
                {Object.entries(upd.qualitative || {}).map(([key, val]) => {
                  if (!val) return null;
                  const field = division.qualitative_fields?.find(f => f.key === key);
                  return (
                    <div key={key} className="text-xs mb-1">
                      <span className="font-medium" style={{ color: '#6B7280' }}>{field?.label || key}: </span>
                      <span style={{ color: '#1B2A4A' }}>{typeof val === 'boolean' ? (val ? 'Yes' : 'No') : val}</span>
                    </div>
                  );
                })}

                {/* CEO Comments */}
                {upd.ceo_comments?.length > 0 && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
                    {upd.ceo_comments.map((c, ci) => (
                      <div key={ci} className="flex items-start gap-2 mb-1">
                        <MessageSquare size={12} style={{ color: '#2A7B88', marginTop: 2 }} />
                        <div>
                          <span className="text-xs font-medium" style={{ color: '#2A7B88' }}>{c.user_name}: </span>
                          <span className="text-xs" style={{ color: '#1B2A4A' }}>{c.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add comment (CEO only) */}
                {isCeo && idx === 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 px-3 py-1.5 rounded-md border text-sm"
                      style={{ borderColor: '#E5E1DC' }}
                      data-testid="ceo-comment-input"
                    />
                    <button onClick={() => handleComment(upd.id)}
                      className="p-1.5 rounded-md text-white" style={{ backgroundColor: '#2A7B88' }}
                      data-testid="ceo-comment-submit">
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
// DIVISION P&L — Sum(outlet contributions) − HQ overhead
// ============================================================
function DivisionPnL({ divisionId }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    api.get(`/divisions/${divisionId}/pnl?period=${period}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchData, [divisionId, period]);

  const fC = (v) => {
    if (!v || v === 0) return '0';
    const n = Number(v);
    const neg = n < 0 ? '-' : '';
    const abs = Math.abs(n);
    if (abs >= 10000000) return `${neg}${(abs / 10000000).toFixed(2)}Cr`;
    if (abs >= 100000) return `${neg}${(abs / 100000).toFixed(1)}L`;
    if (abs >= 1000) return `${neg}${(abs / 1000).toFixed(0)}K`;
    return `${neg}${abs.toLocaleString('en-IN')}`;
  };

  if (loading) return <p className="py-10 text-center text-sm" style={{ color: '#9CA3AF' }}>Calculating P&L...</p>;
  if (!data) return <p className="py-10 text-center text-sm" style={{ color: '#D4726A' }}>Unable to load P&L.</p>;

  const t = data.totals;
  const periods = [
    { id: 'daily', label: 'Today' },
    { id: 'weekly', label: 'This Week' },
    { id: 'monthly', label: 'This Month' },
    { id: 'quarterly', label: 'This Quarter' },
    { id: 'ytd', label: 'YTD' },
  ];

  // ─────────────────────────────────────────────────────────────────
  // CONTRACT-STYLE P&L (LC/CC/CS) — no Food/Labour/Outlet columns
  // ─────────────────────────────────────────────────────────────────
  if (data.model === 'contract') {
    const fmt = (v) => fC(v);
    const pct = (n) => `${Math.round(n * 100) / 100}%`;
    return (
      <div className="space-y-6" data-testid="contract-pnl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold" style={{ color: '#1B2A4A' }}>{data.division_name} — Profit &amp; Loss</h3>
            <p className="text-xs" style={{ color: '#6B7280' }}>{data.period_label} · {data.period_start} to {data.period_end}</p>
          </div>
          <div className="flex gap-1">
            {periods.map(p => (
              <button key={p.id} type="button" onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${period === p.id ? 'text-white' : ''}`}
                style={{ backgroundColor: period === p.id ? '#1B2A4A' : '#F3F4F6', color: period === p.id ? 'white' : '#6B7280' }}
                data-testid={`pnl-period-${p.id}`}>{p.label}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-xl p-4 border" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: '#6B7280' }}>REVENUE (Realised)</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: '#1B2A4A' }}>{fmt(t.revenue)}</p>
            <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>From b2b_revenue ledger</p>
          </div>
          <div className="rounded-xl p-4 border" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: '#6B7280' }}>TIER 1 · After Direct Costs</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: t.tier1_after_direct >= 0 ? '#2D8B5E' : '#DC2626' }}>{fmt(t.tier1_after_direct)}</p>
            <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>− Direct People · − Other Direct</p>
          </div>
          <div className="rounded-xl p-4 border" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: '#6B7280' }}>TIER 2 · After Marketing &amp; Shared</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: t.tier2_after_marketing_and_shared >= 0 ? '#2D8B5E' : '#DC2626' }}>{fmt(t.tier2_after_marketing_and_shared)}</p>
            <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>− Marketing · − Shared People</p>
          </div>
          <div className="rounded-xl p-4 border-2" style={{ borderColor: t.tier3_net_pnl >= 0 ? '#2D8B5E' : '#DC2626', backgroundColor: 'white' }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: '#6B7280' }}>TIER 3 · Division Net P&amp;L</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: t.tier3_net_pnl >= 0 ? '#2D8B5E' : '#DC2626' }}>{fmt(t.tier3_net_pnl)}</p>
            <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>− HQ Overhead · {pct(t.margin_pct)} margin</p>
          </div>
        </div>

        <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: '#F9F7F4' }}>
              <tr>
                <th className="text-left px-4 py-2 font-medium" style={{ color: '#6B7280' }}>Line</th>
                <th className="text-right px-4 py-2 font-medium" style={{ color: '#6B7280' }}>Amount</th>
                <th className="text-right px-4 py-2 font-medium" style={{ color: '#6B7280' }}>% of Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 font-semibold" style={{ color: '#1B2A4A' }}>Revenue (Realised)</td>
                <td className="px-4 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>{fmt(t.revenue)}</td>
                <td className="px-4 py-2 text-right">100%</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− Direct People (cost-allocated + manual)</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.direct_people)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct(t.direct_people / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− Technology</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.technology || 0)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct((t.technology || 0) / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− Operations</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.operations || 0)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct((t.operations || 0) / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− Other Direct Costs</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.other_direct)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct(t.other_direct / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t-2" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9F7F4' }}>
                <td className="px-4 py-2 font-semibold" style={{ color: '#1B2A4A' }}>Tier 1 · After Direct Costs</td>
                <td className="px-4 py-2 text-right font-semibold" style={{ color: t.tier1_after_direct >= 0 ? '#2D8B5E' : '#DC2626' }}>{fmt(t.tier1_after_direct)}</td>
                <td className="px-4 py-2 text-right font-semibold">{t.revenue ? pct(t.tier1_after_direct / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− Marketing</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.marketing)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct(t.marketing / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− Shared People (property/common)</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.shared_people)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct(t.shared_people / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t-2" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9F7F4' }}>
                <td className="px-4 py-2 font-semibold" style={{ color: '#1B2A4A' }}>Tier 2 · After Marketing &amp; Shared</td>
                <td className="px-4 py-2 text-right font-semibold" style={{ color: t.tier2_after_marketing_and_shared >= 0 ? '#2D8B5E' : '#DC2626' }}>{fmt(t.tier2_after_marketing_and_shared)}</td>
                <td className="px-4 py-2 text-right font-semibold">{t.revenue ? pct(t.tier2_after_marketing_and_shared / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 pl-8" style={{ color: '#6B7280' }}>− HQ Overhead (HQ Bengaluru staff)</td>
                <td className="px-4 py-2 text-right" style={{ color: '#DC2626' }}>({fmt(t.hq_people)})</td>
                <td className="px-4 py-2 text-right">{t.revenue ? pct(t.hq_people / t.revenue * 100) : '—'}</td>
              </tr>
              <tr className="border-t-2" style={{ borderColor: t.tier3_net_pnl >= 0 ? '#2D8B5E' : '#DC2626', backgroundColor: t.tier3_net_pnl >= 0 ? '#ECFDF5' : '#FEF2F2' }}>
                <td className="px-4 py-2 font-semibold" style={{ color: '#1B2A4A' }}>Tier 3 · Division Net P&amp;L</td>
                <td className="px-4 py-2 text-right font-semibold" style={{ color: t.tier3_net_pnl >= 0 ? '#2D8B5E' : '#DC2626' }}>{fmt(t.tier3_net_pnl)}</td>
                <td className="px-4 py-2 text-right font-semibold" style={{ color: t.tier3_net_pnl >= 0 ? '#2D8B5E' : '#DC2626' }}>{pct(t.margin_pct)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {data.revenue_by_client && data.revenue_by_client.length > 0 && (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
            <div className="px-4 py-2 font-semibold" style={{ color: '#1B2A4A', backgroundColor: '#F9F7F4' }}>Revenue by Client</div>
            <table className="w-full text-sm">
              <tbody>
                {data.revenue_by_client.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                    <td className="px-4 py-2" style={{ color: '#1B2A4A' }}>{r.client}</td>
                    <td className="px-4 py-2 text-right" style={{ color: '#1B2A4A' }}>{fmt(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CostEntryEditor divisionId={data.division_id} period={period} periodStart={data.period_start} periodEnd={data.period_end} onSaved={() => fetchData()} />

        <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
          Contract-style P&amp;L for {data.division_name}. Revenue is realised cheques from b2b_revenue (not contracted/projected). People costs route via cost_allocation per division. HQ Bengaluru staff appear in Tier 3 for any division they're allocated to.
        </p>
      </div>
    );
  }

  // Waterfall: Revenue → -Food → -Labour → -Other → = Outlet Contribution → -Shared Property → = Property P&L → -HQ → = Division Net P&L
  // Use stacked bars technique: base (invisible) + value
  const tiers = data.tiers || {};
  const sharedTotal = tiers.tier2_shared_property?.total || 0;
  const propertyPnL = tiers.tier2_property_pnl ?? t.contribution;
  const hqTotal = tiers.tier3_hq_overhead?.total ?? data.hq_overhead.total;
  const divisionNetPnL = tiers.tier3_division_net_pnl ?? data.net_pnl;

  const waterfallSteps = [
    { name: 'Revenue', value: t.revenue, type: 'revenue', cumul: t.revenue },
    { name: '- Food Cost', value: -t.food_cost, type: 'negative', cumul: t.revenue - t.food_cost },
    { name: '- Labour', value: -t.labour_cost, type: 'negative', cumul: t.revenue - t.food_cost - t.labour_cost },
    { name: '- Other', value: -t.other_costs, type: 'negative', cumul: t.contribution },
    { name: 'Outlet Contribution', value: t.contribution, type: 'subtotal', cumul: t.contribution },
    { name: '- Shared Property', value: -sharedTotal, type: 'negative', cumul: propertyPnL },
    { name: 'Property P&L', value: propertyPnL, type: 'subtotal', cumul: propertyPnL },
    { name: '- HQ Overhead', value: -hqTotal, type: 'negative', cumul: divisionNetPnL },
    { name: 'Division Net P&L', value: divisionNetPnL, type: 'total', cumul: divisionNetPnL },
  ];
  // Compute base (invisible) + delta for each step
  const waterfallData = [];
  for (let i = 0; i < waterfallSteps.length; i++) {
    const s = waterfallSteps[i];
    if (s.type === 'revenue' || s.type === 'subtotal' || s.type === 'total') {
      waterfallData.push({ name: s.name, base: 0, positive: s.value > 0 ? s.value : 0, negative: s.value < 0 ? Math.abs(s.value) : 0, label: s.value, fill: s.type === 'revenue' ? '#1B2A4A' : (s.type === 'total' ? (s.value >= 0 ? '#2D8B5E' : '#DC2626') : '#185FA5') });
    } else {
      // Negative step — base = cumul after this step (smaller), bar height = |value|
      const prevCumul = waterfallSteps[i - 1].cumul;
      waterfallData.push({ name: s.name, base: s.cumul, negative: Math.abs(s.value), positive: 0, label: s.value, fill: '#D4726A', prev: prevCumul });
    }
  }

  return (
    <div className="space-y-4" data-testid="division-pnl">
      {/* Period selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{data.division_name} — Profit & Loss</p>
          <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{data.period_label} · {data.period_start} to {data.period_end}</p>
        </div>
        <div className="flex rounded-md border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          {periods.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className="px-3 py-1.5 text-xs"
              style={{ backgroundColor: period === p.id ? '#1B2A4A' : '#fff', color: period === p.id ? '#fff' : '#6B7280' }}
              data-testid={`pnl-period-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* 3-TIER Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="pnl-tiers">
        <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Revenue</p>
          <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{fC(t.revenue)}</p>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: '#185FA540', backgroundColor: '#EFF6FF' }} data-testid="pnl-tier1">
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#185FA5' }}>Tier 1 · Outlet Contribution</p>
          <p className="text-xl font-bold" style={{ color: t.contribution > 0 ? '#2D8B5E' : '#DC2626' }}>{fC(t.contribution)}</p>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>{t.contribution_pct}% of revenue · sum of {data.outlets.length} outlets</p>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: '#D9770640', backgroundColor: '#FFFBEB' }} data-testid="pnl-tier2">
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#D97706' }}>Tier 2 · Property P&L</p>
          <p className="text-xl font-bold" style={{ color: propertyPnL > 0 ? '#2D8B5E' : '#DC2626' }}>{fC(propertyPnL)}</p>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>Outlets − Shared Property {fC(sharedTotal)}</p>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: divisionNetPnL >= 0 ? '#10B98140' : '#DC262640', backgroundColor: divisionNetPnL >= 0 ? '#ECFDF5' : '#FEF2F2' }} data-testid="pnl-tier3">
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#2D8B5E' }}>Tier 3 · Division Net P&L</p>
          <p className="text-xl font-bold" style={{ color: divisionNetPnL >= 0 ? '#2D8B5E' : '#DC2626' }}>{fC(divisionNetPnL)}</p>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>Property − HQ {fC(hqTotal)} · {tiers.tier3_division_net_pnl_pct ?? data.net_pnl_pct}% margin</p>
        </div>
      </div>

      {/* Waterfall chart (Section 3D) */}
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="pnl-waterfall">
        <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Profitability Waterfall — {data.period_label}</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={waterfallData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={fC} />
            <Tooltip formatter={(val, name, props) => {
              if (name === 'base') return null;
              return [fC(props?.payload?.label), props?.payload?.name];
            }} />
            <Bar dataKey="base" stackId="a" fill="transparent" />
            <Bar dataKey="positive" stackId="a" isAnimationActive={true}>
              {waterfallData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
            </Bar>
            <Bar dataKey="negative" stackId="a" fill="#D4726A" isAnimationActive={true} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-4 text-[10px] flex-wrap" style={{ color: '#6B7280' }}>
          <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#1B2A4A' }}/>Revenue</span>
          <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#D4726A' }}/>Deductions</span>
          <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#185FA5' }}/>Tier 1-2 Subtotals</span>
          <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: divisionNetPnL >= 0 ? '#2D8B5E' : '#DC2626' }}/>Division Net P&L</span>
        </div>
      </div>

      {/* Outlet table */}
      <div className="bg-white rounded-xl border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-xs">
          <thead className="border-b" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }}>
            <tr>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Outlet</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Revenue</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Food Cost</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Food %</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Labour</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Labour %</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Other</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Contribution</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>%</th>
            </tr>
          </thead>
          <tbody>
            {data.outlets.length === 0 && (
              <tr><td colSpan={9} className="text-center py-6" style={{ color: '#9CA3AF' }}>No outlet data for this period.</td></tr>
            )}
            {data.outlets.map((r, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-[#F7F5F2]" style={{ borderColor: '#E5E1DC' }}>
                <td className="px-3 py-2">
                  <OutletLink name={r.outlet} divId={divisionId} className="text-xs font-medium" />
                </td>
                <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{fC(r.revenue)}</td>
                <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{fC(r.food_cost)}</td>
                <td className="px-3 py-2 text-right" style={{ color: r.food_pct > 35 ? '#DC2626' : r.food_pct > 30 ? '#D97706' : '#2D8B5E' }}>{r.food_pct}%</td>
                <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{fC(r.labour_cost)}</td>
                <td className="px-3 py-2 text-right" style={{ color: r.labour_pct > 30 ? '#DC2626' : r.labour_pct > 25 ? '#D97706' : '#2D8B5E' }}>{r.labour_pct}%</td>
                <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{fC(r.other_costs)}</td>
                <td className="px-3 py-2 text-right font-semibold" style={{ color: r.contribution > 0 ? '#2D8B5E' : '#DC2626' }}>{fC(r.contribution)}</td>
                <td className="px-3 py-2 text-right text-[10px]" style={{ color: '#9CA3AF' }}>{r.contribution_pct}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }}>
            <tr data-testid="pnl-row-outlet-total">
              <td className="px-3 py-2 font-semibold" style={{ color: '#185FA5' }}>Tier 1 · Outlet Contribution</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>{fC(t.revenue)}</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>{fC(t.food_cost)}</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#6B7280' }}>{t.food_pct}%</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>{fC(t.labour_cost)}</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#6B7280' }}>{t.labour_pct}%</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>{fC(t.other_costs)}</td>
              <td className="px-3 py-2 text-right font-bold" style={{ color: t.contribution > 0 ? '#2D8B5E' : '#DC2626' }}>{fC(t.contribution)}</td>
              <td className="px-3 py-2 text-right font-bold text-[11px]" style={{ color: '#1B2A4A' }}>{t.contribution_pct}%</td>
            </tr>
            <tr data-testid="pnl-row-shared">
              <td className="px-3 py-2 font-medium" style={{ color: '#D97706' }}>− Shared Property (Tier 2)</td>
              <td className="px-3 py-2 text-[10px]" colSpan={6} style={{ color: '#9CA3AF' }}>
                Labour {fC(tiers.tier2_shared_property?.labour || 0)} + Other {fC(tiers.tier2_shared_property?.other || 0)} · Common areas & shared staff (e.g. Ketti Common Area)
              </td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#D97706' }}>({fC(sharedTotal)})</td>
              <td></td>
            </tr>
            <tr data-testid="pnl-row-property">
              <td className="px-3 py-2 font-bold" style={{ color: '#D97706' }}>Tier 2 · Property P&L</td>
              <td colSpan={6}></td>
              <td className="px-3 py-2 text-right font-bold text-sm" style={{ color: propertyPnL >= 0 ? '#2D8B5E' : '#DC2626' }}>{fC(propertyPnL)}</td>
              <td className="px-3 py-2 text-right font-bold" style={{ color: '#6B7280' }}>{tiers.tier2_property_pnl_pct ?? '—'}%</td>
            </tr>
            <tr data-testid="pnl-row-hq">
              <td className="px-3 py-2 font-medium" style={{ color: '#D97706' }}>− HQ Overhead (Tier 3)</td>
              <td className="px-3 py-2 text-[10px]" colSpan={6} style={{ color: '#9CA3AF' }}>
                Labour {fC(tiers.tier3_hq_overhead?.labour || data.hq_overhead.labour)} + Other {fC(tiers.tier3_hq_overhead?.other || data.hq_overhead.payables)} · HQ Bengaluru staff & unallocated group costs
              </td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#D97706' }}>({fC(hqTotal)})</td>
              <td></td>
            </tr>
            <tr data-testid="pnl-row-division">
              <td className="px-3 py-2 font-bold" style={{ color: '#1B2A4A' }}>Tier 3 · Division Net P&L</td>
              <td colSpan={6}></td>
              <td className="px-3 py-2 text-right font-bold text-sm" style={{ color: divisionNetPnL >= 0 ? '#2D8B5E' : '#DC2626' }}>{fC(divisionNetPnL)}</td>
              <td className="px-3 py-2 text-right font-bold" style={{ color: divisionNetPnL >= 0 ? '#2D8B5E' : '#DC2626' }}>{tiers.tier3_division_net_pnl_pct ?? data.net_pnl_pct}%</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="bg-[#F7F5F2] rounded-lg p-3">
        <p className="text-[10px]" style={{ color: '#6B7280' }}>
          <strong>3-Tier Formula (Option A):</strong> Tier 1 <em>Outlet Contribution</em> = Revenue − Food Cost − Labour − Other Direct Costs (per outlet, sum).
          Tier 2 <em>Property P&L</em> = Outlet Contribution − Shared Property Costs (Ketti Common Area staff + common areas).
          Tier 3 <em>Division Net P&L</em> = Property P&L − HQ Overhead (HQ Bengaluru staff + unallocated group costs).
          Labour is routed via each employee's cost_category (direct / shared_property / hq_group).
        </p>
        {data.cost_centres?.length > 0 && (
          <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
            <strong>Cost centres in this division:</strong>{' '}
            {data.cost_centres.map((c, i) => (
              <span key={i}>{c.outlet} <em>({c.tier === 'hq' ? 'Tier 3 — HQ' : 'Tier 2 — Shared Property'})</em>{i < data.cost_centres.length - 1 ? ', ' : ''}</span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
}


// ============================================================
// COST ENTRY EDITOR — manual monthly cost entry for contract divisions.
// Lets Nisha/Lohita type People/Tech/Mkt/Ops/Other amounts per month.
// ============================================================
function CostEntryEditor({ divisionId, period, periodStart, periodEnd, onSaved }) {
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ month: '', category: 'people', amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const monthFromPeriod = (periodStart || '').slice(0, 7);

  const refresh = () => {
    api.get(`/divisions/${divisionId}/cost-entries${period === 'monthly' && monthFromPeriod ? `?month=${monthFromPeriod}` : ''}`)
      .then(r => setEntries(r.data))
      .catch(() => setEntries([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refresh, [divisionId, period, periodStart]);

  const fC = (v) => {
    const n = Number(v) || 0;
    if (n >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return n.toLocaleString('en-IN');
  };

  const startAdd = () => {
    setForm({ month: monthFromPeriod || new Date().toISOString().slice(0, 7), category: 'people', amount: '', notes: '' });
    setEditing(null);
    setShowAdd(true);
  };
  const startEdit = (e) => {
    setForm({ month: e.month, category: e.category, amount: e.amount, notes: e.notes || '' });
    setEditing(e.id);
    setShowAdd(true);
  };
  const cancel = () => { setShowAdd(false); setEditing(null); };

  const save = async () => {
    if (!form.month || !form.category || form.amount === '') {
      window.alert('Month, category and amount are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/divisions/${divisionId}/cost-entries`, {
        month: form.month,
        category: form.category,
        amount: Number(form.amount),
        notes: form.notes,
      });
      setShowAdd(false);
      setEditing(null);
      refresh();
      if (onSaved) onSaved();
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete ${entry.category} cost of ₹${fC(entry.amount)} for ${entry.month}?`)) return;
    await api.delete(`/divisions/${divisionId}/cost-entries/${entry.id}`);
    refresh();
    if (onSaved) onSaved();
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
      <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#F9F7F4' }}>
        <span className="font-semibold" style={{ color: '#1B2A4A' }}>Manual Cost Entries {period === 'monthly' && monthFromPeriod ? `(${monthFromPeriod})` : '(all periods)'}</span>
        <button onClick={startAdd} className="text-xs px-3 py-1 rounded-md text-white font-medium" style={{ backgroundColor: '#2A7B88' }} data-testid="add-cost-entry">+ Add Cost</button>
      </div>
      {entries.length === 0 && !showAdd && (
        <p className="px-4 py-4 text-xs text-center" style={{ color: '#9CA3AF' }}>No manual cost entries yet for this period. Click + Add Cost to record People / Tech / Marketing / Operations / Other costs.</p>
      )}
      {entries.length > 0 && (
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#FAFAFA' }}>
            <tr>
              <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Month</th>
              <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Category</th>
              <th className="text-right px-4 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Amount</th>
              <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Notes</th>
              <th className="text-right px-4 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-4 py-2 text-sm" style={{ color: '#1B2A4A' }}>{e.month}</td>
                <td className="px-4 py-2 text-sm capitalize" style={{ color: '#1B2A4A' }}>{e.category}</td>
                <td className="px-4 py-2 text-sm text-right" style={{ color: '#1B2A4A' }}>₹{fC(e.amount)}</td>
                <td className="px-4 py-2 text-xs" style={{ color: '#6B7280' }}>{e.notes || '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => startEdit(e)} className="text-[11px] px-2 py-0.5 rounded border mr-1 hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Edit</button>
                  <button onClick={() => remove(e)} className="text-[11px] px-2 py-0.5 rounded border hover:bg-red-50" style={{ borderColor: '#FEE2E2', color: '#D4726A' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showAdd && (
        <div className="border-t px-4 py-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9F7F4' }}>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Month</label>
            <input type="month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Category</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: '#E5E1DC' }}>
              <option value="people">People</option>
              <option value="technology">Technology</option>
              <option value="marketing">Marketing</option>
              <option value="operations">Operations</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Amount (₹)</label>
            <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="cost-amount" />
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Notes</label>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <div className="flex gap-1">
            <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2A7B88' }} data-testid="save-cost-entry">{saving ? 'Saving…' : (editing ? 'Update' : 'Save')}</button>
            <button onClick={cancel} className="px-3 py-1.5 rounded-md text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
